import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import type { SupportAttachmentKind, SupportSubmitPayload } from '../../types/support';
import {
  SUPPORT_FILE_ACCEPT,
  SUPPORT_MAX_FILES,
  SUPPORT_MAX_TEXT_LENGTH,
  classifySupportFile,
  validateSupportFile,
} from '../../utils/supportAttachments';
import { validateOutgoingVoice } from '../../utils/supportVoice';
import SupportFilePreview from './SupportFilePreview';
import SupportVoiceRecorderBar from './SupportVoiceRecorderBar';

interface Props {
  disabled?: boolean;
  sending?: boolean;
  placeholder?: string;
  onSubmit: (payload: SupportSubmitPayload) => Promise<void> | void;
}

interface PendingFile {
  id: string;
  file: File;
  kind: SupportAttachmentKind;
  previewUrl: string;
  durationSec?: number;
}

const generateId = (): string => `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const SupportChatInput: React.FC<Props> = ({
  disabled = false,
  sending = false,
  placeholder = 'Опишите проблему...',
  onSubmit,
}) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragCounterRef = useRef(0);

  const voice = useVoiceRecorder({
    onError: (msg) => setWarning(msg),
  });

  useEffect(() => {
    return () => {
      files.forEach((f) => {
        try {
          URL.revokeObjectURL(f.previewUrl);
        } catch {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 140;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [text]);

  useEffect(() => {
    if (!warning) return;
    const id = window.setTimeout(() => setWarning(null), 4000);
    return () => window.clearTimeout(id);
  }, [warning]);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (disabled) return;
      const accepted: PendingFile[] = [];
      let firstError: string | null = null;
      let availableSlots = SUPPORT_MAX_FILES - files.length;

      for (const file of incoming) {
        if (availableSlots <= 0) {
          firstError = firstError || `Можно прикрепить не более ${SUPPORT_MAX_FILES} файлов`;
          break;
        }
        const err = validateSupportFile(file);
        if (err) {
          firstError = firstError || err;
          continue;
        }
        const kind = classifySupportFile(file);
        if (!kind) {
          firstError =
            firstError ||
            `«${file.name}»: неподдерживаемый тип. Допустимы изображения, видео, аудио и документы`;
          continue;
        }
        accepted.push({
          id: generateId(),
          file,
          kind,
          previewUrl: URL.createObjectURL(file),
        });
        availableSlots -= 1;
      }

      if (accepted.length > 0) {
        setFiles((prev) => [...prev, ...accepted]);
      }
      if (firstError) setWarning(firstError);
    },
    [disabled, files.length],
  );

  const handleRemove = useCallback((id: string) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch {}
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter((it) => it.type.startsWith('image/'));
      if (imageItems.length === 0) return;
      const pastedFiles: File[] = [];
      for (const it of imageItems) {
        const file = it.getAsFile();
        if (file) pastedFiles.push(file);
      }
      if (pastedFiles.length > 0) {
        e.preventDefault();
        addFiles(pastedFiles);
      }
    },
    [addFiles],
  );

  const hasVoiceInQueue = useMemo(() => files.some((f) => f.kind === 'voice'), [files]);
  const attachmentCount = useMemo(
    () => files.filter((f) => f.kind !== 'voice').length + (hasVoiceInQueue ? 1 : 0),
    [files, hasVoiceInQueue],
  );

  const canSend = useMemo(() => {
    const trimmed = text.trim();
    return !sending && !disabled && (trimmed.length > 0 || files.length > 0);
  }, [sending, disabled, text, files.length]);

  const buildSubmitPayload = useCallback(
    (trimmedText: string, pending: PendingFile[]): SupportSubmitPayload => {
      const voicePending = pending.find((f) => f.kind === 'voice');
      const rest = pending.filter((f) => f.kind !== 'voice');
      return {
        text: trimmedText,
        attachments: rest.map((f) => ({
          file: f.file,
          durationSec: f.durationSec,
        })),
        voice: voicePending
          ? { file: voicePending.file, durationSec: voicePending.durationSec }
          : undefined,
      };
    },
    [],
  );

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    const trimmed = text.trim();
    if (trimmed.length > SUPPORT_MAX_TEXT_LENGTH) {
      setWarning(`Текст слишком длинный (макс. ${SUPPORT_MAX_TEXT_LENGTH} символов)`);
      return;
    }
    const payload = buildSubmitPayload(trimmed, files);
    setText('');
    files.forEach((f) => {
      try {
        URL.revokeObjectURL(f.previewUrl);
      } catch {}
    });
    setFiles([]);
    await onSubmit(payload);
  }, [buildSubmitPayload, canSend, files, onSubmit, text]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  const handleDragEnter: React.DragEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      dragCounterRef.current += 1;
      const hasFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
      if (hasFiles) setIsDragging(true);
    },
    [disabled],
  );

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop: React.DragEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;
      if (disabled) return;
      const dropped = Array.from(e.dataTransfer.files || []);
      if (dropped.length > 0) addFiles(dropped);
    },
    [addFiles, disabled],
  );

  const handleAttachClick = useCallback(() => {
    if (disabled || voice.isActive) return;
    fileInputRef.current?.click();
  }, [disabled, voice.isActive]);

  const addVoiceToQueue = useCallback(
    (file: File, previewUrl: string, durationSec: number) => {
      if (disabled) return;
      if (files.some((f) => f.kind === 'voice')) {
        setWarning('В одном сообщении можно отправить только одно голосовое');
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
        return;
      }
      if (attachmentCount >= SUPPORT_MAX_FILES) {
        setWarning(`Можно прикрепить не более ${SUPPORT_MAX_FILES} файлов (включая голосовое)`);
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
        return;
      }
      const err = validateOutgoingVoice(file);
      if (err) {
        setWarning(err);
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
        return;
      }
      setFiles((prev) => [
        ...prev,
        {
          id: generateId(),
          file,
          kind: 'voice',
          previewUrl,
          durationSec,
        },
      ]);
    },
    [attachmentCount, disabled, files],
  );

  const handleMicClick = useCallback(() => {
    if (disabled || sending) return;
    if (voice.phase === 'recording') {
      voice.stopRecording();
      return;
    }
    if (voice.isActive) return;
    if (files.some((f) => f.kind === 'voice')) {
      setWarning('В одном сообщении можно отправить только одно голосовое');
      return;
    }
    if (attachmentCount >= SUPPORT_MAX_FILES) {
      setWarning(`Можно прикрепить не более ${SUPPORT_MAX_FILES} файлов (включая голосовое)`);
      return;
    }
    void voice.startRecording();
  }, [attachmentCount, disabled, files, sending, voice]);

  const handleSendVoicePreview = useCallback(async () => {
    const taken = voice.takePreview();
    if (!taken) return;
    const trimmed = text.trim();
    const hasOther = files.length > 0;

    if (trimmed || hasOther) {
      addVoiceToQueue(taken.file, taken.previewUrl, taken.durationSec);
      return;
    }

    await onSubmit({
      text: '',
      attachments: [],
      voice: { file: taken.file, durationSec: taken.durationSec },
    });
    try {
      URL.revokeObjectURL(taken.previewUrl);
    } catch {}
  }, [addVoiceToQueue, files.length, onSubmit, text, voice]);

  const showRecorder =
    voice.phase === 'requesting' ||
    voice.phase === 'recording' ||
    voice.phase === 'preview';

  return (
    <div
      className={`sc-input-zone ${isDragging ? 'is-dragging' : ''} ${disabled ? 'is-disabled' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="sc-drop-overlay" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span>Отпустите, чтобы прикрепить</span>
        </div>
      )}

      {warning && (
        <div className="sc-input-warning" role="alert">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {warning}
        </div>
      )}

      {files.length > 0 && (
        <div className="sc-input-previews">
          {files.map((f) => (
            <SupportFilePreview
              key={f.id}
              kind={f.kind}
              name={f.file.name}
              size={f.file.size}
              previewUrl={f.previewUrl}
              durationSec={f.durationSec}
              onRemove={() => handleRemove(f.id)}
            />
          ))}
        </div>
      )}

      {showRecorder && voice.phase === 'preview' && voice.preview && (
        <SupportVoiceRecorderBar
          mode="preview"
          previewUrl={voice.preview.previewUrl}
          durationSec={voice.preview.durationSec}
          onDiscard={voice.discardPreview}
          onSend={() => void handleSendVoicePreview()}
          sending={sending}
        />
      )}

      {showRecorder && (voice.phase === 'recording' || voice.phase === 'requesting') && (
        <SupportVoiceRecorderBar
          mode={voice.phase === 'requesting' ? 'requesting' : 'recording'}
          durationSec={voice.durationSec}
          liveBars={voice.liveBars}
          onCancel={voice.cancelRecording}
          onStop={voice.stopRecording}
        />
      )}

      <div className={`sc-input-row ${showRecorder ? 'sc-input-row--hidden' : ''}`}>
        <button
          type="button"
          className="sc-icon-btn sc-icon-btn--attach"
          onClick={handleAttachClick}
          disabled={disabled || attachmentCount >= SUPPORT_MAX_FILES || voice.isActive}
          aria-label="Прикрепить файл"
          title={
            attachmentCount >= SUPPORT_MAX_FILES
              ? `Максимум ${SUPPORT_MAX_FILES} вложений`
              : 'Прикрепить файл (изображение, видео, аудио, документ)'
          }
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept={SUPPORT_FILE_ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            const selected = Array.from(e.target.files || []);
            if (selected.length > 0) addFiles(selected);
            e.target.value = '';
          }}
        />

        <button
          type="button"
          className={`sc-icon-btn sc-icon-btn--mic ${voice.phase === 'recording' ? 'is-recording' : ''}`}
          onClick={handleMicClick}
          disabled={
            disabled ||
            sending ||
            voice.phase === 'requesting' ||
            voice.phase === 'preview' ||
            attachmentCount >= SUPPORT_MAX_FILES ||
            hasVoiceInQueue
          }
          aria-label={
            voice.phase === 'recording' ? 'Остановить запись' : 'Записать голосовое сообщение'
          }
          title={
            voice.phase === 'recording'
              ? 'Остановить запись'
              : 'Голосовое сообщение (нужен доступ к микрофону)'
          }
        >
          {voice.phase === 'recording' ? (
            <span className="sc-mic-recording-icon" aria-hidden="true" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          )}
        </button>

        <textarea
          ref={textareaRef}
          className="sc-textarea"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, SUPPORT_MAX_TEXT_LENGTH + 200))}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled || voice.isActive}
          rows={1}
        />

        <button
          type="button"
          className="sc-send-btn"
          onClick={() => void handleSend()}
          disabled={!canSend || voice.isActive}
          aria-label="Отправить"
          title="Отправить (Enter)"
        >
          {sending ? (
            <span className="sc-send-spinner" aria-hidden="true" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </div>

      <div className="sc-input-hint">
        <span>
          {showRecorder
            ? 'Нажмите «стоп», когда закончите · микрофон запрашивается при первой записи'
            : 'Enter — отправить · Shift+Enter — перенос · микрофон — голосовое'}
        </span>
        {text.length > SUPPORT_MAX_TEXT_LENGTH * 0.85 && (
          <span className={text.length > SUPPORT_MAX_TEXT_LENGTH ? 'sc-counter sc-counter--over' : 'sc-counter'}>
            {text.length}/{SUPPORT_MAX_TEXT_LENGTH}
          </span>
        )}
      </div>
    </div>
  );
};

export default SupportChatInput;
